import React from 'react';
import { aiLinkRoute, explorerLink } from './aiLinkRoute';

// Tiny markdown -> React elements renderer for the AI chat (design D10).
// Never uses dangerouslySetInnerHTML: the model echoes database text, so every
// character reaches the DOM as a React text node. Tolerant of half-streamed
// input: an unclosed fence / emphasis / code span / link renders as plain text.
//
// Supported: paragraphs, #..### headings, **bold**, *italic*, `code`, fenced
// code, ordered/unordered lists (one nesting level), GFM pipe tables (every
// <td data-label="<header>"> so the house .table--stack phone cards apply),
// ---, [text](href) links and bare explorer URLs.
//
// renderMarkdown(text, { onNavigate }) -> React node
//   onNavigate(route) is called with { path, state } for in-app links.
// Links: in-app via aiLinkRoute; https block-explorer hosts in a new tab;
// anything else is shown as plain text (never an <a>).

const LIST_RE = /^(\s*)([-*+]|\d{1,3}[.)])\s+(.*)$/;
const HEADING_RE = /^(#{1,3})\s+(.+?)\s*#*\s*$/;
const HR_RE = /^\s*-{3,}\s*$/;
const FENCE_RE = /^\s*```/;
const TABLE_SEP_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const BARE_URL_RE = /https:\/\/[^\s<>()[\]]+/g;
const LINK_RE = /^\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/;
const ESCAPABLE = '\\`*[]()#|>-_';

// First index >= from of `token` that closes an emphasis run, or -1.
function findClose(s, from, token) {
  let at = s.indexOf(token, from);
  while (at >= 0) {
    const prev = s[at - 1];
    const next = s[at + token.length];
    const spacedBefore = prev === ' ' || prev === '\n';
    if (!spacedBefore && !(token === '*' && next === '*')) return at;
    at = s.indexOf(token, at + 1);
  }
  return -1;
}

function inline(s, ctx, inLink) {
  const out = [];
  let buf = '';
  let i = 0;
  const key = () => `${ctx.k++}`;
  const flush = () => {
    if (!buf) return;
    out.push(...linkifyBare(buf, ctx, inLink));
    buf = '';
  };

  while (i < s.length) {
    const c = s[i];
    if (c === '\\' && i + 1 < s.length && ESCAPABLE.includes(s[i + 1])) {
      buf += s[i + 1];
      i += 2;
      continue;
    }
    if (c === '`') {
      const end = s.indexOf('`', i + 1);
      if (end > i + 1) {
        flush();
        out.push(<code key={key()} className="aic-code-inline">{s.slice(i + 1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    if (c === '[' && !inLink) {
      const m = LINK_RE.exec(s.slice(i));
      if (m) {
        flush();
        out.push(linkNode(m[1], m[2], ctx));
        i += m[0].length;
        continue;
      }
    }
    if (c === '*' && s[i + 1] === '*') {
      const end = findClose(s, i + 2, '**');
      if (end > i + 2) {
        flush();
        out.push(<strong key={key()}>{inline(s.slice(i + 2, end), ctx, inLink)}</strong>);
        i = end + 2;
        continue;
      }
      buf += '**';
      i += 2;
      continue;
    }
    if (c === '*' && s[i + 1] !== ' ' && s[i + 1] !== '\n' && s[i + 1] !== undefined) {
      const end = findClose(s, i + 1, '*');
      if (end > i + 1) {
        flush();
        out.push(<em key={key()}>{inline(s.slice(i + 1, end), ctx, inLink)}</em>);
        i = end + 1;
        continue;
      }
    }
    buf += c;
    i += 1;
  }
  flush();
  return out;
}

// Plain text segment -> text nodes with bare https explorer URLs made links.
function linkifyBare(text, ctx, inLink) {
  if (inLink || text.indexOf('https://') < 0) return [text];
  const parts = [];
  let last = 0;
  let m;
  BARE_URL_RE.lastIndex = 0;
  while ((m = BARE_URL_RE.exec(text)) !== null) {
    const url = m[0].replace(/[.,;:!?]+$/, '');
    const ext = explorerLink(url);
    if (!ext) continue;
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<a key={`${ctx.k++}`} className="aic-link" href={ext} target="_blank" rel="noopener noreferrer">{url}</a>);
    last = m.index + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

function linkNode(label, href, ctx) {
  const k = `${ctx.k++}`;
  const route = aiLinkRoute(href);
  if (route) {
    return (
      <a
        key={k}
        className="aic-link"
        href={route.path}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
          e.preventDefault();
          if (ctx.onNavigate) ctx.onNavigate(route);
        }}
      >
        {inline(label || href, ctx, true)}
      </a>
    );
  }
  const ext = explorerLink(href);
  if (ext) {
    return (
      <a key={k} className="aic-link" href={ext} target="_blank" rel="noopener noreferrer">
        {inline(label || ext, ctx, true)}
      </a>
    );
  }
  // Not a whitelisted link: keep the words, drop the target.
  return <React.Fragment key={k}>{inline(label, ctx, true)}</React.Fragment>;
}

// Text with newlines -> inline nodes separated by <br/>.
function inlineLines(lines, ctx) {
  const out = [];
  lines.forEach((ln, idx) => {
    if (idx > 0) out.push(<br key={`${ctx.k++}`} />);
    out.push(...inline(ln, ctx, false));
  });
  return out;
}

function splitRow(line) {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|') && !t.endsWith('\\|')) t = t.slice(0, -1);
  const cells = [];
  let cur = '';
  let tick = false;
  for (let i = 0; i < t.length; i += 1) {
    const ch = t[i];
    if (ch === '\\' && t[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (ch === '`') tick = !tick;
    if (ch === '|' && !tick) { cells.push(cur.trim()); cur = ''; continue; }
    cur += ch;
  }
  cells.push(cur.trim());
  return cells;
}

const isBlockStart = (line, next) =>
  FENCE_RE.test(line) || HEADING_RE.test(line) || HR_RE.test(line) || LIST_RE.test(line)
  || (line.includes('|') && next !== undefined && next.includes('|') && TABLE_SEP_RE.test(next));

function parseList(lines, start, ctx) {
  // Collect items (+ one nested level) until a non-list line.
  const items = [];
  let i = start;
  while (i < lines.length) {
    let line = lines[i];
    if (!line.trim()) {
      // a blank line keeps the list going only if another item follows
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j += 1;
      if (j < lines.length && LIST_RE.test(lines[j])) { i = j; line = lines[i]; } else break;
    }
    const m = LIST_RE.exec(line);
    if (m) {
      const indent = m[1].replace(/\t/g, '  ').length;
      const entry = { ordered: /\d/.test(m[2]), num: parseInt(m[2], 10), text: m[3], kids: [] };
      if (indent >= 2 && items.length) items[items.length - 1].kids.push(entry);
      else items.push(entry);
      i += 1;
    } else if (/^\s{2,}\S/.test(line) && items.length && !isBlockStart(line, lines[i + 1])) {
      const host = items[items.length - 1];
      const target = host.kids.length ? host.kids[host.kids.length - 1] : host;
      target.text += ` ${line.trim()}`;
      i += 1;
    } else break;
  }
  return { items, next: i };
}

function renderItems(items, ctx) {
  // Group neighbours of the same kind into <ol>/<ul>.
  const groups = [];
  items.forEach((it) => {
    const g = groups[groups.length - 1];
    if (g && g.ordered === it.ordered) g.items.push(it);
    else groups.push({ ordered: it.ordered, items: [it] });
  });
  return groups.map((g) => {
    const Tag = g.ordered ? 'ol' : 'ul';
    const startAt = g.ordered && g.items[0].num > 1 ? g.items[0].num : undefined;
    return (
      <Tag key={`${ctx.k++}`} className="aic-list" start={startAt}>
        {g.items.map((it) => (
          <li key={`${ctx.k++}`}>
            {inline(it.text, ctx, false)}
            {it.kids.length ? renderItems(it.kids.map((kid) => ({ ...kid, kids: [] })), ctx) : null}
          </li>
        ))}
      </Tag>
    );
  });
}

export function renderMarkdown(text, { onNavigate } = {}) {
  const ctx = { k: 0, onNavigate };
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }

    if (FENCE_RE.test(line)) {
      let end = i + 1;
      while (end < lines.length && !FENCE_RE.test(lines[end])) end += 1;
      if (end < lines.length) {
        blocks.push(
          <pre key={`${ctx.k++}`} className="aic-code"><code>{lines.slice(i + 1, end).join('\n')}</code></pre>
        );
        i = end + 1;
      } else {
        // never closed (still streaming): plain text
        blocks.push(<p key={`${ctx.k++}`} className="aic-plain">{lines.slice(i).join('\n')}</p>);
        i = lines.length;
      }
      continue;
    }

    const h = HEADING_RE.exec(line);
    if (h) {
      const level = h[1].length;
      const Tag = `h${level + 2}`; // # -> h3 ... ### -> h5 (the panel is small)
      blocks.push(<Tag key={`${ctx.k++}`} className={`aic-h aic-h${level}`}>{inline(h[2], ctx, false)}</Tag>);
      i += 1;
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push(<hr key={`${ctx.k++}`} className="aic-hr" />);
      i += 1;
      continue;
    }

    if (line.includes('|') && lines[i + 1] !== undefined && lines[i + 1].includes('|') && TABLE_SEP_RE.test(lines[i + 1])) {
      const head = splitRow(line);
      let j = i + 2;
      const rows = [];
      while (j < lines.length && lines[j].trim() && lines[j].includes('|')) {
        rows.push(splitRow(lines[j]));
        j += 1;
      }
      blocks.push(
        <div key={`${ctx.k++}`} className="aic-table-wrap">
          <table className="aic-table table--stack">
            <thead>
              <tr>{head.map((c) => <th key={`${ctx.k++}`}>{inline(c, ctx, false)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${ctx.k++}`}>
                  {head.map((label, ci) => (
                    <td key={`${ctx.k++}`} data-label={label} className={ci === 0 ? 'cell-title' : undefined}>
                      {inline(r[ci] || '', ctx, false)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      i = j;
      continue;
    }

    if (LIST_RE.test(line)) {
      const { items, next } = parseList(lines, i, ctx);
      blocks.push(...renderItems(items, ctx));
      i = next;
      continue;
    }

    // paragraph: run of ordinary lines
    const para = [line];
    i += 1;
    while (i < lines.length && lines[i].trim() && !isBlockStart(lines[i], lines[i + 1])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push(<p key={`${ctx.k++}`} className="aic-p">{inlineLines(para, ctx)}</p>);
  }
  return blocks;
}

export default renderMarkdown;
